import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bCouponService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupon-service'
import { useFpB2bSyncGiveawayValuesToForm } from '@cart-services/hooks/fp-b2b/use-fp-b2b-sync-giveaway-values-to-form'
import { type GiveawayValue, type RegularGiveawayValue } from '@cart-services/types'
import { type CartFormValues } from '@cart-services/zod-schema/cartFormSchema'
import { useWpCartResponsive } from '@cart-ui/WpCartLayout/useWpCartResponsive'
import { Box } from '@pets-ui/Box/Box'
import { Grid } from '@pets-ui/Grid/Grid'
import { VStack } from '@pets-ui/VStack/VStack'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useStmCart } from './CartProvider'
import { CardCoupon } from './components/CardCoupon/CardCoupon'
import { CardOrderDetail } from './components/CardOrderDetail'
import { CartAddonProducts } from './components/CartAddonProducts/CartAddonProducts'
import { CartBrandHeader } from './components/CartBrandHeader'
import { CartEmpty } from './components/CartEmpty'
import { DialogCartThreshold } from './components/CartFooter/DialogCartThreshold'
import { DialogGiftNotEnough } from './components/CartFooter/DialogGiftNotEnough'
import { CartGiveaways } from './components/CartGiveaways/CartGiveaways'
import { CartListItems } from './components/CartListItems/CartListItems'
import { CartRegularGiveawayTips } from './components/CartRegularGiveawayTips/CartRegularGiveawayTips'

export const Cart = () => {
  const isMobile = useWpCartResponsive('mobile')
  const { campaignId, projectCode, countryCode, campaignCode, deliveryClass, platform } = useStmCart()
  const { isCartEmpty } = useFpB2bCartService(campaignId, platform)
  const { clearInvalidSelectedGiveaways, clearInvalidManualInputCouponIds } = useFpB2bCouponService(campaignId, projectCode, countryCode, campaignCode, platform)
  const { setValue } = useFormContext<CartFormValues>()

  const handleGiveawayValuesChange = (giveawayValues?: GiveawayValue[]) => {
    setValue('giveaways', giveawayValues)
  }

  const handleRegularGiveawayValuesChange = (regularGiveawayValues?: RegularGiveawayValue[]) => {
    setValue('regularGiveaways', regularGiveawayValues)
  }

  useFpB2bSyncGiveawayValuesToForm({
    campaignId,
    campaignCode,
    projectCode,
    countryCode,
    deliveryClass,
    onGiveawayValuesChange: handleGiveawayValuesChange,
    onRegularGiveawayValuesChange: handleRegularGiveawayValuesChange,
    platform,
  })

  useEffect(() => {
    clearInvalidSelectedGiveaways()
  }, [clearInvalidSelectedGiveaways])

  useEffect(() => {
    clearInvalidManualInputCouponIds()
  }, [clearInvalidManualInputCouponIds])

  if (isCartEmpty) {
    return <CartEmpty />
  }

  return (
    <>
      <VStack gap={3}>
        <Grid container gap={3} columns={14}>
          <Grid size={isMobile ? 14 : 10}>
            <VStack gap={3}>
              <VStack>
                <CartBrandHeader />
                <CartListItems />
              </VStack>
              <CartGiveaways />
              <CartRegularGiveawayTips />
              <CartAddonProducts />
              {isMobile && (
                <VStack gap={3}>
                  <CardCoupon />
                  <CardOrderDetail />
                </VStack>
              )}
            </VStack>
          </Grid>
          {!isMobile && (
            <Grid size={4}>
              <Box position="sticky" top={0}>
                <VStack gap={3}>
                  <CardCoupon />
                  <CardOrderDetail />
                </VStack>
              </Box>
            </Grid>
          )}
        </Grid>
      </VStack>
      <DialogGiftNotEnough />
      <DialogCartThreshold />
    </>
  )
}
